import { generateUUID } from './Generator';
import { Chart, ChartEditorOptions } from '../domain/chart/ChartDomains';
import { ChartType, Syntax } from '@trlogic/ui-pack';
import {
  ClusterSegment,
  DateHistogramSegment,
  FilterSegment,
  FilterSegmentItem,
  GroupBySegment,
  GroupSegmentField,
  GroupSegmentOrderBy,
  RangeClusterRange,
  SegmentAppearance,
} from '../domain/chart/editor/Segment';
import { Metric, MetricAppearance } from '../domain/chart/editor/Metric';
import moment from 'moment';
import { TableVariableType } from '../../../server/src/lib/domain/chart/ChartDomain';
import VariableType from '../constant/VariableType';
import DataType from '../constant/DataType';
import { FormicaSelectBoxItem } from '../components/custom/FormicaSearchSelectBox';
import { initializePalette } from '../components/custom/color-palette/ColorPaletteFactory';
import { ArrayVariableProperties, ObjectVariableProperties } from '../domain/datatype/VariablePropertiesDomains';
import ChartUtil from './ChartUtil';
import { ChartOptions } from 'chart.js';

class ChartValueFactory {
  public static initializeDateHistogramSegment = (): DateHistogramSegment => {
    return {
      id: 'dateHistogram',
      appearance: this.initializeSegmentAppearance(),
      field: {} as TableVariableType,
      interval: '',
      ignoreIntervalByDocumentCount: false,
    } as DateHistogramSegment;
  };
  public static initializeClusterSegment = (): ClusterSegment => {
    return {
      id: 'clustering',
      appearance: this.initializeSegmentAppearance(),
      field: {} as TableVariableType,
      range: {
        type: 'range',
        interval: 10,
      } as RangeClusterRange,
      collapseBy: '',
      colorPalette: '',
    } as ClusterSegment;
  };
  public static initializeGroupBySegment = (): GroupBySegment => {
    return {
      id: 'groupBy',
      appearance: this.initializeSegmentAppearance(),
      field: [] as GroupSegmentField[],
      size: 5,
      orderBy: {
        type: 'alphabetical',
        field: {
          name: 'alphabetical',
          dataType: DataType.STRING,
        } as TableVariableType,
        order: 'asc',
      } as GroupSegmentOrderBy,
      groupOthers: true,
    } as GroupBySegment;
  };
  public static initializeFilterSegment = (): FilterSegment => {
    return {
      id: 'New Filter',
      appearance: this.initializeSegmentAppearance(),
      items: [{ label: 'New Filter', syntax: this.initializeExpression() }] as FilterSegmentItem[],
    };
  };

  public static initializeMetric = (count: number): Metric => {
    return {
      id: generateUUID(),
      type: 'Count',
      field: { name: 'Record', value: 'Record', dataType: DataType.STRING } as TableVariableType,
      filter: this.initializeExpression(),
      transformer: this.initializeExpression(),
      appearance: {
        name: `Count of Record ${count > 0 ? '-' + count : ''}`,
        suffix: '',
        prefix: '',
        colorSettings: {
          colorPalette: initializePalette().Blue,
          activeColor: ChartUtil.getColors()[count % 10],
        },
        decimal: 0,
      } as MetricAppearance,
    } as Metric;
  };

  private static initializeSegmentAppearance = (): SegmentAppearance => {
    return {
      name: '',
      decimal: 0,
      suffix: '',
      prefix: '',
    } as SegmentAppearance;
  };

  public static initializeExpression(): Syntax {
    return { id: generateUUID(), value: { type: 'TokenGroup' }, children: [] } as Syntax;
  }

  public static initializeEmptySyntax = (): Syntax => {
    return { id: generateUUID(), value: { type: 'TokenGroup' }, children: [] } as Syntax;
  };

  public static initializeChart = (): Chart => {
    return {
      id: undefined,
      name: '',
      description: '',
      dataSource: '',
      type: ChartType.LINE,
      createdDate: new Date().valueOf(),
      updatedDate: new Date().valueOf(),
      visualOptions: this.initializeChartOptions(),
    } as Chart;
  };

  public static initializeChartOptions = (): Partial<ChartOptions> => {
    return ChartUtil.getLineOptions({
      name: 'Line Chart',
      metrics: [],
      legendDisplay: true,
    });
  };

  public static initializeChartEditorOptions = (): ChartEditorOptions => {
    return {
      transactionVariables: [],
      segmentEditorVisible: false,
      metricEditorVisible: false,
      startDate: { label: '3 year ago', value: moment().subtract(3, 'year').toDate(), type: 'Relative' },
      endDate: { label: 'Now', value: moment().toDate(), type: 'Now' },
      segmentItem: null,
      metricItem: [],
      activeMetricItem: null,
      breakdown: null,
      breakdownEditorVisible: false,
      globalFilterItem: { label: 'Global Filter', syntax: this.initializeExpression() },
      globalFilterEditorVisible: false,
    };
  };
  public static initializeElasticVariables = (riskType: string, transactionId: string): any[] => {
    return [
      {
        type: VariableType.EVENT_VARIABLE as any,
        variable: {
          type: 'Common Variable',
          variableType: VariableType.EVENT_VARIABLE as any,
          properties: {
            dataType: DataType.OBJECT,
            variables: [
              {
                name: `${riskType}`,
                properties: {
                  dataType: DataType.OBJECT,
                  variables: [
                    {
                      name: 'score',
                      properties: {
                        dataType: DataType.NUMBER,
                      },
                    },
                    {
                      name: 'resolution',
                      properties: {
                        dataType: DataType.STRING,
                      },
                    },
                    {
                      name: 'status',
                      properties: {
                        dataType: DataType.STRING,
                      },
                    },
                    {
                      name: 'rules',
                      properties: {
                        dataType: DataType.ARRAY,
                        properties: {
                          dataType: DataType.OBJECT,
                          variables: [
                            {
                              name: 'name',
                              properties: {
                                dataType: DataType.STRING,
                              },
                            },
                            {
                              name: 'score',
                              properties: {
                                dataType: DataType.NUMBER,
                              },
                            },
                            {
                              name: 'resolution',
                              properties: {
                                dataType: DataType.STRING,
                              },
                            },
                          ],
                        } as ObjectVariableProperties,
                      } as ArrayVariableProperties,
                    },
                  ],
                } as ObjectVariableProperties,
              },
            ],
          } as ObjectVariableProperties,
          name: `result`,
          args: {
            isElasticVariable: true,
            event: transactionId,
          },
        },
      },
    ];
  };

  public static initializeElasticVariablesForChartMetricAndSegment = (riskType: string): FormicaSelectBoxItem[] => {
    return [
      {
        label: 'Created at (default)',
        value: 'createdAt',
        dataType: DataType.DATE,
      },
      {
        label: `Score (default)`,
        value: `result.${riskType}.score`,
        dataType: DataType.NUMBER,
      },
      {
        label: `Status (default)`,
        value: `result.${riskType}.status.keyword`,
        dataType: DataType.STRING,
      },
      {
        label: `Resolution (default)`,
        value: `result.${riskType}.resolution.keyword`,
        dataType: DataType.STRING,
      },
      {
        label: `Rule Names (default)`,
        value: `result.${riskType}.rules.name.keyword`,
        dataType: DataType.STRING,
      },
      {
        label: `Rule Scores (default)`,
        value: `result.${riskType}.rules.score`,
        dataType: DataType.NUMBER,
      },
      {
        label: `Assignee (default)`,
        value: `result.${riskType}.metadata.assignee.id.keyword`,
        dataType: DataType.STRING,
      },
      {
        label: `Time to First Assigment (default)`,
        value: `result.${riskType}.metadata.assignee.assignmentDuration`,
        dataType: DataType.NUMBER,
      },
      {
        label: `Time to Done (default)`,
        value: `result.${riskType}.metadata.review.duration`,
        dataType: DataType.NUMBER,
      },
    ];
  };
}

export interface CustomSelectOptions {
  label: string;
  value: string;
}

export default ChartValueFactory;
